import RegularText from '@/components/RegularText';
import { getOptionsSelectedAdd, getOptionsSelectedRemove } from '@/helpers';
import { stringify, uniq } from '@/helpers/string';
import { IAutoComplete, IOptions } from '@/types';
import { Autocomplete, Badge, LegacyStack, Tag } from '@shopify/polaris';
import { memo, useCallback, useEffect, useState } from 'react';

function AutoCompleteAllowAdd({ ...props }: IAutoComplete.IPropsAllowAdd) {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<IOptions[]>([]);
  const [error, setError] = useState('');
  const [tags, setTags] = useState<Array<IOptions>>([]);

  useEffect(() => {
    setTags(props.tag);
  }, [props.tag]);

  useEffect(() => {
    updateText(inputValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringify(props.options)]);

  const handleSetError = useCallback(
    (msg: string) => {
      props?.customSetError?.(msg);
      setError(msg);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(props?.customSetError)],
  );

  useEffect(() => {
    if (!props.requiredIndicator || props.selectedOptions.length > 0) {
      handleSetError('');
    }
  }, [handleSetError, props.requiredIndicator, props.selectedOptions.length]);

  const updateText = (value: string) => {
    let newValue = value;
    if (props.removeSeparator) newValue = newValue.replaceAll(props.removeSeparator, '');
    if (error) {
      handleSetError('');
    }
    if (props.handleSearch) {
      props.handleSearch(newValue);
    }
    setInputValue(newValue);
    const resultOptions = props.options?.filter((option) => option.label.toLocaleLowerCase().includes(value.toLowerCase()));
    setOptions(resultOptions);
  };
  const handleReturnValue = (selected: string[], isNew: boolean) => {
    const isAdd = selected.length > props.selectedOptions.length;
    const result = !props.allowMultiple
      ? [props.options.find((item) => item.value === selected[0]) || { value: '', label: '' }]
      : isAdd
      ? isNew
        ? [...tags, { label: selected[selected.length - 1], value: selected[selected.length - 1] }]
        : getOptionsSelectedAdd(selected, tags, props.options)
      : getOptionsSelectedRemove(selected, tags);
    return uniq(result.map((item) => ({ ...item, disabled: !!item.disabled })));
  };

  const removeTag = (tag: string) => () => {
    const newTags = tags?.filter((item) => item.value !== tag);
    setTags(newTags);
    props.setSelectedOptions(
      handleReturnValue(
        newTags.map((item) => item.value),
        false,
      ),
    );
    if (newTags.length === 0) {
      handleSetError('This field is required');
    }
  };

  const handleOnSelect = (value: string[]) => {
    props.setSelectedOptions(handleReturnValue(value, false));
    const newTags = uniq(value.map((tag) => props.options.find((item) => item.value === tag) || { value: '', label: '' }));
    setTags(newTags);
    if (error) {
      handleSetError('');
    }
  };

  const handleAddNew = () => {
    props.setOptions([...props.options, { label: inputValue, value: inputValue, disabled: false }]);
    setTags([...tags, { label: inputValue, value: inputValue, disabled: false }]);
    props.setSelectedOptions(handleReturnValue([...props.selectedOptions, inputValue], true));
    props.clearInputWhenAdd && setInputValue('');
  };

  const textField = (
    <Autocomplete.TextField
      error={props.requiredIndicator ? error : undefined}
      onChange={updateText}
      label={props.label}
      value={inputValue}
      autoComplete="off"
      requiredIndicator={props.requiredIndicator}
      placeholder={props.placeholder}
      disabled={props.disable}
      helpText={props.helpText}
      onBlur={
        props.requiredIndicator
          ? () => {
              if (props.selectedOptions.length === 0) {
                handleSetError('This field is required');
              }
            }
          : undefined
      }
    />
  );

  const tagsMarkup =
    tags.length > 0
      ? tags?.map((option) => {
          return (
            <Tag key={`option${option.value}`} onRemove={removeTag(option.value)}>
              {option.label || ''}
            </Tag>
          );
        })
      : null;
  const selectedTagMarkup = tags.length > 0 ? <LegacyStack spacing="extraTight">{tagsMarkup}</LegacyStack> : null;
  return (
    <div>
      <Autocomplete
        allowMultiple={props.allowMultiple}
        options={options.map((item) => {
          if (item?.badgeContent) {
            return {
              ...item,
              label: (
                <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                  <RegularText>{item.label}</RegularText>
                  <Badge tone="attention">{item.badgeContent}</Badge>
                </div>
              ),
            };
          }
          return item;
        })}
        selected={props.selectedOptions}
        textField={textField}
        onSelect={handleOnSelect}
        actionBefore={
          props.allowAdd && inputValue && !options.find((item) => item.value === inputValue)
            ? {
                content: `Add new ${inputValue}`,
                onAction: handleAddNew,
              }
            : undefined
        }
      />
      <div style={{ marginTop: '8px' }}>{selectedTagMarkup}</div>
    </div>
  );
}

export default memo(AutoCompleteAllowAdd);
